import { ReactNode, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

interface PageHeroProps {
  tag?: string;
  title: ReactNode;
  subtitle: string;
  buttons?: { label: string; href: string; variant: "primary" | "outline" }[];
  backgroundImage?: string;
  backgroundVideo?: string;
  fullHeight?: boolean;
}

const PageHero = ({ tag, title, subtitle, buttons, backgroundImage, backgroundVideo, fullHeight }: PageHeroProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const location = useLocation();

  // Restart video on mount / route change
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }, [location.pathname]);

  // Restart video when hero scrolls into view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
  <section className={`relative ${fullHeight ? "min-h-screen" : "min-h-[60vh] md:min-h-[75vh]"} flex items-center overflow-hidden`}>
    {/* Background */}
    {backgroundVideo ? (
      <>
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-contain sm:object-cover scale-100 sm:scale-105 object-center md:object-[70%_center]"
            poster={backgroundImage}
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy-deep/40 to-navy-deep/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/70 via-transparent to-transparent" />
      </>
    ) : backgroundImage ? (
      <>
        <div className="absolute inset-0">
          <img src={backgroundImage} alt="" className="w-full h-full object-cover scale-105" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/80 via-navy-deep/60 to-navy-deep/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/80 via-transparent to-transparent" />
      </>
    ) : (
      <div className="absolute inset-0 grid-overlay" style={{ background: 'linear-gradient(175deg, hsl(218 55% 10%) 0%, hsl(215 45% 14%) 100%)' }} />
    )}

    {/* Decorative elements */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal/50 to-transparent" />
    <div className="absolute bottom-0 left-0 w-[300px] h-[1px] bg-gradient-to-r from-teal to-transparent" />

    <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-20 md:py-32 relative z-10 w-full">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {tag && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-[2px] bg-gradient-to-r from-teal to-lime" />
            <span className="text-teal text-[11px] font-bold tracking-[4px] uppercase font-body">{tag}</span>
          </motion.div>
        )}
        <h1 className="font-display text-[clamp(2rem,6vw,5.5rem)] leading-[1.1] text-primary-foreground max-w-[850px] mb-6 md:mb-8 tracking-tight">
          {title}
        </h1>
        <p className="text-primary-foreground/50 font-body text-lg md:text-xl max-w-[550px] mb-12 leading-relaxed font-light">
          {subtitle}
        </p>
        {buttons && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap gap-4"
          >
            {buttons.map((btn) => (
              <Link
                key={btn.label}
                to={btn.href}
                className={`group inline-flex items-center gap-2.5 px-8 py-4 text-[12px] font-bold tracking-[1.5px] uppercase rounded-full transition-all duration-400 font-body ${
                  btn.variant === "primary"
                    ? "bg-gradient-to-r from-teal to-teal-light text-navy-deep hover:shadow-xl hover:shadow-teal/25 hover:scale-[1.02]"
                    : "border border-primary-foreground/25 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:border-primary-foreground/40"
                }`}
              >
                {btn.label}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </motion.div>
        )}
      </motion.div>
    </div>

    {/* Bottom fade */}
    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
  </section>
  );
};

export default PageHero;
